import { getRoot, SnapshotIn, types } from 'mobx-state-tree';
import { Instance } from 'mobx-state-tree';
import { callAgroApi } from '@services/agroapi';
import { IPlace, Place } from './PlaceStore';
import { formatIncomeUnits, isPresent, luxonUserLocale } from '@farmai-shared/LFUtils';
import i18n from '@services/next-i18n';
import { DateTimeType, withDateProcessors } from './DateTimeUtility';
import { TransactionResponse } from '@services/responseTypes';
import { IRootStoreAny } from './rootUtilType';
export interface ITransactions {
  id: string;
  cropzone_id: string;
  source?: {
    id: string;
    name: string;
  };
  destination: {
    id: string;
    name: string;
  };
  datetime: string;
  cropQualityUnit: string | null | undefined;
  qtyExchanged: string;
  extra: {
    income: number | null;
    crop_quality: number | null;
  };
}
export const Transaction = withDateProcessors(types.model('Transaction', {
  id: types.string,
  source: types.maybeNull(types.safeReference(Place, {
    get(identifier, parent): IPlace {
      const uiStore = getRoot<IRootStoreAny>(parent).uiStore;
      return uiStore.currentOrganization?.placeStore.places.get(identifier);
    },
    set(value) {
      return value.name;
    }
  })),
  destination: types.safeReference(Place, {
    get(identifier, parent): IPlace {
      const uiStore = getRoot<IRootStoreAny>(parent).uiStore;
      return uiStore.currentOrganization?.placeStore.places.get(identifier);
    },
    set(value) {
      return value.name;
    }
  }),
  timezone: types.string,
  qtyExchanged: types.string,
  datetime: DateTimeType,
  extra: types.map(types.maybeNull(types.union(types.number, types.string))),
  cropzone_id: types.string
}).views(self => ({
  get root() {
    return getRoot<IRootStoreAny>(self);
  }
})).actions(self => ({
  update(params: {
    cropzone_id: string;
    source_id?: string | null;
    destination_id: string;
    datetime: string;
    qty_exchanged: number;
    extra: {
      income?: number;
      income_unit?: string;
      crop_quality?: number;
      crop_quality_unit?: string;
    };
  }) {
    return callAgroApi<TransactionResponse>(`/txes/${self.id}`, 'PATCH', params);
  },
  delete() {
    return callAgroApi<TransactionResponse>(`/txes/${self.id}`, 'DELETE');
  }
})).views(self => ({
  get income() {
    const num = self.extra.get('income') ? parseFloat(self.extra.get('income') as string) : null;
    if (!isPresent(num)) return;
    if (self.extra.get('income_unit') === 'baht') {
      return num;
    }
    return num / 100;
  },
  get incomeUnit() {
    return i18n.t(formatIncomeUnits(self.extra.get('income_unit') as string) || 'baht');
  },
  get cropQuality() {
    return self.extra.get('crop_quality') ? parseFloat(self.extra.get('crop_quality') as string) : null;
  },
  get cropQualityUnit(): string | null {
    return self.extra.get('crop_quality_unit') === 'string' ? self.extra.get('crop_quality_unit') as string : null;
  },
  get datetimeZ() {
    const lang = self.root.userStore.user.settings.lang;
    return self.datetime.setZone(self.timezone).reconfigure({
      outputCalendar: lang === 'th' ? 'Buddhist' : 'gregory'
    }).setLocale(luxonUserLocale(lang));
  }
})));
export type ITransaction = Instance<typeof Transaction>;
export type ITransactionSnapshotIn = SnapshotIn<typeof Transaction>;
export const TxStore = types.model('TransactionStore', {
  transactions: types.map(Transaction)
}).actions(self => ({
  replaceTxes(txes: ITransactions[]) {
    const txMap = txes.map(tx => [tx.id, tx]);
    self.transactions.replace(txMap);
    const result = txes.map(tx => {
      const stored = self.transactions.get(tx.id);
      return stored;
    });
    return result;
  }
}));
export type ITransactionStore = Instance<typeof TxStore>;